import React, { useState, useRef } from "react";
import { HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Table, Input, Button, Space } from "antd";
import type { InputRef } from 'antd';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import "./data_wh_transfer_out.css";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

interface DataType {
    key: React.Key;
    transferOutNo: string;
    transferDate: string;
    warehouseName: string;
    warehouseCode: string;
    status: string;
    transferRequestNo: string;
}

interface ApiResponse {
    data: DataType[];
    total: number;
}

type DataIndex = keyof DataType;
const fetcher = (url: string) => axios.get(url).then(res => res.data);

const DataTransferWhOut: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const navigate = useNavigate();
    const { userData } = useAuth();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [sort, setSort] = useState<{ field?: string, order?: string }>({});
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [isNavigating, setIsNavigating] = useState(false);

    const buildQueryString = () => {
        const params = new URLSearchParams();
        if (userData?.warehouse_code) params.append('warehouseCode', userData.warehouse_code);
        params.append('page', pagination.current.toString());
        params.append('pageSize', pagination.pageSize.toString());
        if (sort.field) {
            params.append('sortField', sort.field);
            params.append('sortOrder', sort.order || '');
        }
        Object.entries(filters).forEach(([key, val]) => {
            if (val) params.append(key, val);
        });
        return params.toString();
    };

    const { data, error, isLoading } = useSWR<ApiResponse>(
        userData?.warehouse_code ? `${process.env.REACT_APP_BACKEND_URL}/api/transfer-outs?${buildQueryString()}` : null,
        fetcher
    );
    const getStatusText = (status: string): string => {
        switch (status) {
            case '1':
                return 'Draft';
            case '2':
                return 'Accept';
            case '3':
                return 'Ready';
            case '4':
                return 'Reject';
            case '5':
                return 'Closed';
            case '6':
                return 'intransit';
            default:
                return 'Unknown';
        }
    };


    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
        setFilters(prev => ({ ...prev, [dataIndex]: selectedKeys[0] }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
        clearFilters();
        setSearchText('');
        setFilters(prev => {
            const updated = { ...prev };
            delete updated[dataIndex];
            return updated;
        });
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) => text,
    });


    const columns: ColumnsType<DataType> = [
        {
            title: 'Transfer Out No',
            dataIndex: 'transferOutNo',
            key: 'transferOutNo',
            sorter: true,
            ...getColumnSearchProps('transferOutNo'),
        },
        {
            title: 'Transfer Date',
            dataIndex: 'transferDate',
            key: 'transferDate',
            sorter: true,
            ...getColumnSearchProps('transferDate'),
            render: (text: string) => {
                if (!text) return '';
                if (/^\d{2}\/\d{2}\/\d{4}$/.test(text)) return text;
                if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
                    const [year, month, day] = text.split('T')[0].split('-');
                    return `${day}/${month}/${year}`;
                }
                const d = new Date(text);
                return !isNaN(d.getTime())
                    ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
                    : text;
            },
        },
        {
            title: 'Warehouse Name',
            dataIndex: 'warehouseName',
            key: 'warehouseName',
            sorter: true,
            ...getColumnSearchProps('warehouseName'),
        },
        {
            title: 'Warehouse Code',
            dataIndex: 'warehouseCode',
            key: 'warehouseCode',
            filters: Array.from(new Set((data?.data || []).map((d: any) => d.warehouseCode))).map((code: string) => {
                const rec = (data?.data || []).find((d: any) => d.warehouseCode === code);
                return { text: rec?.warehouseName || code, value: code };
            }),
            onFilter: (value: any, record: DataType) => record.warehouseCode === value,
            render: (_: any, record: DataType) => record.warehouseName || _,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            ...getColumnSearchProps('status'),
            render: (status: string) => getStatusText(status),
            sorter: true,
        },
        {
            title: 'Transfer Request No',
            dataIndex: 'transferRequestNo',
            key: 'transferRequestNo',
            sorter: true,
            ...getColumnSearchProps('transferRequestNo'),
        },
    ];

    return (

        <div id="iiok" style={transformStyle}>
            <div className="gjs-grid-row" id="i2fk">
                <div className="gjs-grid-column" id="il8s">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span style={{ fontWeight: '200', color: 'rgba(0, 0, 0, 0.88)' }}>Data WH Transfer Out</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/item_wh_transfer_out",
                                title: "Item WH Transfers Out",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="i2jh">
                <div className="gjs-grid-column" id="iueh">
                    <Table
                        columns={columns}
                        dataSource={data?.data}
                        loading={isLoading || isNavigating}
                        style={{ paddingTop: '20px' }}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: data?.total || 0,
                            showSizeChanger: true,
                        }}
                        onChange={(newPagination, _, newSorter) => {
                            setPagination({
                                current: newPagination.current || 1,
                                pageSize: newPagination.pageSize || 10,
                            });
                            if (!Array.isArray(newSorter) && newSorter.field) {
                                setSort({
                                    field: newSorter.field as string,
                                    order: newSorter.order || undefined,
                                });
                            } else {
                                setSort({});
                            }
                        }}
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => {
                                    if (isNavigating) return;
                                    setIsNavigating(true);
                                    navigate('/dashboard/item_wh_transfer_out', { state: { selectedRecord: record } });
                                },
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataTransferWhOut;